import type { DemoAttachment } from '../../demo/groupConsecutiveImageMessages';

export type ConsoleMsgAttachment = {
  kind: string;
  mimeType: string;
  caption?: string;
  url?: string;
  ref?: string;
  durationSeconds?: number;
};

export type ConsoleChatMessage = {
  id: string;
  sender: string;
  content: string;
  attachments?: ConsoleMsgAttachment[];
  isWhisper?: boolean;
  timestamp: string;
};

export type ConsoleChatRenderItem =
  | { type: 'message'; message: ConsoleChatMessage; index: number }
  | {
      type: 'album';
      id: string;
      sender: string;
      images: DemoAttachment[];
      messageIds: string[];
      /** Índice del primer mensaje del álbum en el array original (separadores de día). */
      firstIndex: number;
      timestamp: string;
    };

const EMPTY_CONTENT = new Set(['', '[Archivos adjuntos]', '[Audio]', '📎']);

function isImageAttachment(a: ConsoleMsgAttachment): boolean {
  if (a.kind === 'image') return true;
  if (a.kind === 'video' || a.kind === 'audio' || a.kind === 'document') return false;
  return (a.mimeType || '').toLowerCase().startsWith('image/');
}

function hasNonImageAttachment(msg: ConsoleChatMessage): boolean {
  return (msg.attachments ?? []).some((a) => !isImageAttachment(a));
}

export function imageAttachmentsOf(msg: ConsoleChatMessage): DemoAttachment[] {
  return (msg.attachments ?? [])
    .filter(isImageAttachment)
    .map((a) => ({
      kind: a.kind,
      mimeType: a.mimeType,
      caption: a.caption,
      url: a.url || a.ref || '',
    }))
    .filter((a) => Boolean(a.url));
}

function usefulContent(msg: ConsoleChatMessage): boolean {
  const t = (msg.content ?? '').trim();
  if (!t) return false;
  if (EMPTY_CONTENT.has(t)) return false;
  return true;
}

/** Mensaje solo con fotos (sin texto útil ni otro media). */
export function isImageOnlyMessage(msg: ConsoleChatMessage): boolean {
  if (msg.isWhisper) return false;
  const images = imageAttachmentsOf(msg);
  if (images.length === 0) return false;
  if (hasNonImageAttachment(msg)) return false;
  if (usefulContent(msg)) return false;
  return true;
}

/**
 * Colapsa mensajes consecutivos solo-imagen del mismo sender en un álbum.
 * Mensajes con texto (p. ej. nombre de cabaña) rompen el álbum.
 */
export function groupConsoleImageMessages(messages: ConsoleChatMessage[]): ConsoleChatRenderItem[] {
  const out: ConsoleChatRenderItem[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i]!;
    if (!isImageOnlyMessage(msg)) {
      out.push({ type: 'message', message: msg, index: i });
      i += 1;
      continue;
    }

    const sender = msg.sender;
    const images: DemoAttachment[] = [];
    const messageIds: string[] = [];
    const firstIndex = i;
    const timestamp = msg.timestamp;
    while (
      i < messages.length &&
      messages[i]!.sender === sender &&
      isImageOnlyMessage(messages[i]!)
    ) {
      const cur = messages[i]!;
      images.push(...imageAttachmentsOf(cur));
      messageIds.push(cur.id);
      i += 1;
    }
    out.push({
      type: 'album',
      id: `album-${messageIds[0]}-${messageIds.length}`,
      sender,
      images,
      messageIds,
      firstIndex,
      timestamp,
    });
  }
  return out;
}
